import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Container, Row, Card, CardBody, CardHeader, Nav, NavItem, NavLink, TabContent, TabPane, Form, FormGroup, Input, Label } from 'reactstrap';
import { toast } from 'react-toastify';
import Breadcrumbs from '../CommonElements/Breadcrumbs';
import Table from '../CustomComponent/Tables/Table';
import { TableColumn } from 'react-data-table-component';
import { Eye, Pencil, XCircle } from 'lucide-react';
import ActionDropdown from '../CustomComponent/ActionDropdown';
import Modal from '../CustomComponent/Modals/Modal';
import { Btn } from '../AbstractElements';
import { Href } from '../Utils/Constants';
import { useUser } from '../Contexts/UserContext';

// Services
import { getProperties, addProperty, editProperty as updateProperty, getClientApps, revokeClientAppAccess, grantClientAppAccess, revokeClientUserAccess, addClientUser, grantClientAccess, revokeClientAccess } from '../Services/PortfolioService';
import { getProperties as getClientProperties } from '../Services/ClientService';
import { getTenantApps } from '../Services/AppService';
import { getClientUsers } from '../Services/PortfolioService';
import * as yup from 'yup'; // Import yup
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';

const schema = yup.object().shape({
    first_name: yup.string().trim().required('First name is required'),
    last_name: yup.string().trim().required('Last name is required'),
    email_address: yup
        .string()
        .trim()
        .email('Must be a valid email')
        .required('Email address is required'),
    phone_number: yup
        .string()
        .optional(), // phone is optional, default '' is handled server-side
});

export default function MergedPortfolio() {
    const defaultValues = {
        first_name: '',
        last_name: '',
        email_address: '',
        phone_number: ''
    };

    const { control, watch, setValue, handleSubmit, formState: { errors }, reset } = useForm({
        resolver: yupResolver(schema), // Use the schema for validation
        defaultValues: defaultValues
    });

    const navigate = useNavigate();
    const { type } = useUser();

    const [client, setClient] = useState<any>(null);
    const [clientId, setClientId] = useState<any>(null);
    const [properties, setProperties] = useState<any>([]);

    // Tenant-only states
    const [apps, setApps] = useState<any>([]);
    const [clientApps, setClientApps] = useState<any>([]);
    const justifyTabNav = (type === 'client')
        ? [{ id: 'properties', nav: 'Properties' }, { id: 'app_access', nav: 'App Access' }]
        : [{ id: 'users', nav: 'Users' }, { id: 'properties', nav: 'Properties' }, { id: 'app_access', nav: 'App Access' }];
    const [basicTab, setBasicTab] = useState(type === 'client' ? 'properties' : 'users');
    const [users, setUsers] = useState<any[]>([]);

    // Modal states
    const [propertyModalOpen, setPropertyModalOpen] = useState(false);
    const togglePropertyModal = () => setPropertyModalOpen(!propertyModalOpen);

    const [clientAccessModalOpen, setClientAccessModalOpen] = useState(false);
    const toggleClientAccessModal = () => setClientAccessModalOpen(!clientAccessModalOpen);

    const [userModalOpen, setUserModalOpen] = useState(false);
    const toggleUserModal = () => setUserModalOpen(!userModalOpen);

    const [editModalOpen, setEditModalOpen] = useState(false);
    const toggleEditModal = () => setEditModalOpen(!editModalOpen);

    const [appAccessModalOpen, setAppAccessModalOpen] = useState(false);
    const toggleAppAccessModal = () => setAppAccessModalOpen(!appAccessModalOpen);

    // Form states
    const [editProperty, setEditProperty] = useState<any>(null);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [address, setAddress] = useState('');
    const [contactName, setContactName] = useState('');
    const [appId, setAppId] = useState('');

    const fetchProperties = async () => {
        try {
            const response = (type == 'tenant') ? await getProperties(clientId!) : await getClientProperties();
            setProperties(response.properties);
            setClient(response.client);
        } catch (error: any) {
            toast.error(error?.message || "Failed to fetch Properties");
        }
    };

    const fetchClientApps = async () => {
        try {
            const { data } = await getClientApps(clientId!);
            setClientApps(data);
        } catch (error: any) {
            toast.error(error?.message || "Failed to fetch client apps");
        }
    };

    const fetchClientUsers = async () => {
        try {
            const response = await getClientUsers(clientId!);
            setUsers(response.data);
        } catch (error: any) {
            toast.error(error?.message || "Failed to fetch users");
        }
    };

    const handleClientAccess = async () => {
        try {
            if (client?.status === 'have access') {
                await revokeClientAccess(clientId);
                toast.success("Client access revoked successfully");
            } else {
                await grantClientAccess(clientId); // You may need a proper grant function for client, using grantClientAppAccess with null as workaround
                toast.success("Client access granted successfully");
            }
            fetchClientUsers();
            toggleClientAccessModal();
        } catch (error: any) {
            toast.error(error?.message || "Failed to update client access");
        }
    }

    const handleAddUser: SubmitHandler<any> = async (data) => {
        try {
            await addClientUser(clientId!, data);
            toast.success("User added successfully");
            fetchClientUsers();
            toggleUserModal();
            reset();
        } catch (error: any) {
            toast.error(error.message || "Failed to add user");
        }
    }

    const fetchTenantApps = async () => {
        try {
            const storedTenantApps = localStorage.getItem('tenantApps');
            if (storedTenantApps) {
                setApps(JSON.parse(storedTenantApps));
            } else {
                const tenantAppsData = await getTenantApps();
                localStorage.setItem('tenantApps', JSON.stringify(tenantAppsData));
                setApps(tenantAppsData);
            }
        } catch (error) {
            console.error('Error fetching tenant apps:', error);
        }
    };

    useEffect(() => {
        const storedClient = localStorage.getItem('selectedClient');
        if (storedClient) {
            setClientId(storedClient);
        }
    }, []);

    useEffect(() => {
        if (!clientId) return;
        fetchProperties();
        if (type === 'tenant') {
            fetchTenantApps();
            fetchClientApps();
            fetchClientUsers();
        }
    }, [clientId, type]);

    // Actions
    const propertyActions = [
        {
            label: 'View',
            icon: <Eye size={16} className="text-success" />,
            onClick: (row: any) => navigate(`/client/property/${row.property_id}`),
        },
        ...(type === 'tenant'
            ? [{
                label: 'Edit',
                icon: <Pencil size={16} className="text-warning" />,
                onClick: (row: any) => {
                    setEditProperty(row);
                    setName(row.name);
                    setAddress(row.address);
                    setContactName(row.contact_name);
                    setEmail(row.email_address);
                    toggleEditModal();
                },
            }]
            : [])
    ];

    const appActions = [
        {
            label: 'Revoke Access',
            icon: <XCircle size={16} className="text-danger" />,
            onClick: async (row: any) => {
                try {
                    await revokeClientAppAccess(clientId, row.app_id);
                    toast.success("App Access revoked successfully!");
                    fetchClientApps();
                } catch (error: any) {
                    toast.error(error.message || "Failed to revoke access");
                }
            }
        },
    ];

    // Table columns
    const propertiesTableColumns: TableColumn<any>[] = [
        { name: "S/N", cell: (_row, index) => index + 1, width: '70px' },
        {
            name: "Property Name",
            cell: (row: any) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                    onClick={() => navigate(`/client/property/${row.property_id}`)}>
                    <span className="text-primary">{row.name || ''}</span>
                </div>
            ),
            width: '250px',
            sortable: true
        },
        { name: "Address", selector: (row) => row.address, width: '300px', wrap: true },
        { name: "Contact Name", selector: (row) => row.contact_name },
        { name: "Email Address", selector: (row) => row.email_address, width: '200px', },
        {
            name: 'Actions',
            cell: (row) => <ActionDropdown row={row} actions={propertyActions} />,
            ignoreRowClick: true,
            width: '70px'
        },
    ];

    const appsTableColumns: TableColumn<any>[] = [
        { name: "S/N", cell: (_row, index) => index + 1, width: '70px' },
        { name: "App Name", selector: (row) => row.app.name || '' },
        {
            name: 'Actions',
            cell: (row) => <ActionDropdown row={row} actions={appActions} />,
            ignoreRowClick: true,
            width: '70px'
        },
    ];

    // Handlers
    const handleAddProperty = async () => {
        try {
            await addProperty(clientId!, {
                name, address, email_address: email, contact_name: contactName
            });
            fetchProperties();
            togglePropertyModal();
        } catch (error: any) {
            toast.error(error.message || "Failed to add Property");
        }
    };

    const handleEditProperty = async () => {
        if (!editProperty) return;
        try {
            await updateProperty(clientId!, {
                name, address, email_address: email, contact_name: contactName
            }, editProperty.property_id);
            toast.success("Property updated!");
            fetchProperties();
            toggleEditModal();
        } catch (error: any) {
            toast.error(error.message || "Failed to update property");
        }
    };

    const handleAppAccess = async () => {
        try {
            await grantClientAppAccess(clientId!, appId);
            setAppId('');
            toast.success("App Access granted");
            fetchClientApps();
            toggleAppAccessModal();
        } catch (error: any) {
            toast.error(error.message || "Failed to grant Access");
        }
    };

    const userActions = [
        {
            label: 'Revoke Access',
            icon: <XCircle size={16} className="text-danger" />,
            onClick: async (row: any) => {
                try {
                    await revokeClientUserAccess(clientId, row?.user_id);
                    toast.success("User access revoked successfully!");
                    fetchClientUsers();
                } catch (error: any) {
                    toast.error(error.message || "Failed to revoke access");
                }
            }
        }
    ];

    const usersTableColumns: TableColumn<any>[] = [
        {
            name: "S/N",
            cell: (_row, index) => index + 1,
            width: "70px"
        },
        {
            name: "Full Name",
            selector: (row) => `${row.first_name} ${row.last_name}` || '',
            sortable: true
        },
        {
            name: "Email",
            selector: (row) => row.email_address || '',
            sortable: true
        },
        // {
        //     name: "Role",
        //     selector: (row) => row.role?.name || 'User',
        //     sortable: false
        // },
        {
            name: "Status",
            selector: (row) => row.status === 'active' ? 'Active' : 'Inactive',
            sortable: false
        },
        {
            name: "Actions",
            cell: (row) => (
                <ActionDropdown
                    row={row}
                    actions={userActions}
                />
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "90px"
        }
    ];

    return (
        <>
            <Breadcrumbs pageTitle='Portfolio' />
            <Container fluid className='default-dashboard'>
                {client && (
                    <Row className="mb-4">
                        <Col sm="12">
                            <div className="p-4 border border-primary rounded shadow-sm">
                                <Row className="gy-3">
                                    <Col lg="4"><small>Company Name</small><div className="text-primary fw-bold">{client.name}</div></Col>
                                    <Col lg="4"><small>Email Address</small><div className="text-primary fw-bold">{client.email_address}</div></Col>
                                    <Col lg="4"><small>Country</small><div className="text-primary fw-bold">{client.country?.name}</div></Col>
                                    <Col lg="4"><small>No. of Properties</small><div className="text-primary fw-bold">{properties.length}</div></Col>
                                    <Col lg="8"><small>Address</small><div className="text-primary fw-bold">{client.address}</div></Col>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                )}

                {type === 'tenant' ? (
                    <Row>
                        <Col xxl={12}>
                            <Card>
                                <CardBody>
                                    <CardHeader className='d-flex justify-content-between align-items-center flex-wrap gap-2 pb-2 p-0'>
                                        <Nav className='nav-primary' pills>
                                            {justifyTabNav.map((item) => (
                                                <NavItem key={item.id}>
                                                    <NavLink href={Href} className={`${basicTab === item.id ? 'active' : ''}`}
                                                        onClick={() => setBasicTab(item.id)}>{item.nav}</NavLink>
                                                </NavItem>
                                            ))}
                                        </Nav>
                                    </CardHeader>
                                    <CardBody className='px-0 pb-0'>
                                        <TabContent activeTab={basicTab}>
                                            <TabPane tabId='users'>
                                                <Row>
                                                    <Col sm="12" className="d-flex justify-content-end">
                                                        {client?.status === 'have access' ? (
                                                            <>
                                                                <Btn color='danger' className="mb-3 mx-2" onClick={toggleClientAccessModal}>Revoke Client Access</Btn>
                                                                <Btn color='primary' className="mb-3" onClick={toggleUserModal}>Add User</Btn>
                                                            </>
                                                        ) : (
                                                            <Btn color='primary' className="mb-3 mx-2" onClick={toggleClientAccessModal}>Grant Client Access</Btn>
                                                        )}
                                                    </Col>
                                                    <Col sm="12">
                                                        <Table
                                                            title="Client Users"
                                                            tableColumns={usersTableColumns}
                                                            data={users}
                                                        />
                                                    </Col>
                                                </Row>
                                            </TabPane>
                                            <TabPane tabId='properties'>
                                                <Row>
                                                    <Col sm="12" className="d-flex justify-content-end">
                                                        <Btn color='primary' className="mb-3" onClick={togglePropertyModal}>Add Property</Btn>
                                                    </Col>
                                                    <Table title='Properties' tableColumns={propertiesTableColumns} data={properties} />
                                                </Row>
                                            </TabPane>
                                            <TabPane tabId='app_access'>
                                                <Row>
                                                    <Col sm="12" className="d-flex justify-content-end">
                                                        {/* <Btn color='primary' className="mb-3 mx-2" onClick={togglePropertyModal}>Revoke Client Access</Btn> */}
                                                        <Btn color='primary' className="mb-3 mx-2" onClick={toggleAppAccessModal}>Grant App Access</Btn>
                                                    </Col>
                                                    <Table title='Apps' tableColumns={appsTableColumns} data={clientApps} />
                                                </Row>
                                            </TabPane>
                                        </TabContent>
                                    </CardBody>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                ) : (
                    <Row>
                        <Table title='Properties' tableColumns={propertiesTableColumns} data={properties} />
                    </Row>
                )}

                <Modal
                    isOpen={clientAccessModalOpen}
                    toggle={toggleClientAccessModal}
                    action={handleClientAccess}
                    title={client?.status === 'have access' ? "Revoke Client Access" : "Grant Client Access"}
                    button={client?.status === 'have access' ? "Revoke Access" : "Grant Access"}
                >
                    <div>
                        {client?.status === 'have access'
                            ? <p>Are you sure you want to revoke this client's access?</p>
                            : <p>Are you sure you want to grant this client access?</p>
                        }
                    </div>
                </Modal>

                <Modal isOpen={userModalOpen} toggle={toggleUserModal} action={handleSubmit(handleAddUser)} title="Add New User" button='Add' size='lg'>
                    <Form className="theme-form mt-4 text-start">
                        <Row style={{ marginTop: '5px' }}>
                            <Col sm='3'>
                                <FormGroup>
                                    <Label for="first-name">First Name</Label>
                                    <Controller
                                        name="first_name"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                id="first-name"
                                                placeholder="Enter first name"
                                                type="text"
                                                invalid={!!errors.first_name}
                                            />
                                        )}
                                    />
                                    {errors.first_name && (
                                        <div className="invalid-feedback d-block">{errors.first_name.message}</div>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col sm='3'>
                                <FormGroup>
                                    <Label for="last-name">Last Name</Label>
                                    <Controller
                                        name="last_name"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                id="last-name"
                                                placeholder="Enter last name"
                                                type="text"
                                                invalid={!!errors.last_name}
                                            />
                                        )}
                                    />
                                    {errors.last_name && (
                                        <div className="invalid-feedback d-block">{errors.last_name.message}</div>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col sm='3'>
                                <FormGroup>
                                    <Label for="email-address">Email Address</Label>
                                    <Controller
                                        name="email_address"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                id="email-address"
                                                placeholder="Enter email address"
                                                type="email"
                                                invalid={!!errors.email_address}
                                            />
                                        )}
                                    />
                                    {errors.email_address && (
                                        <div className="invalid-feedback d-block">{errors.email_address.message}</div>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col sm='3'>
                                <FormGroup>
                                    <Label for="phone-number">Phone Number</Label>
                                    <Controller
                                        name="phone_number"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                id="phone-number"
                                                placeholder="Enter phone number (optional)"
                                                type="text"
                                                invalid={!!errors.phone_number}
                                            />
                                        )}
                                    />
                                    {errors.phone_number && (
                                        <div className="invalid-feedback d-block">{errors.phone_number.message}</div>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                    </Form>
                </Modal>

                {/* Modals */}
                <Modal isOpen={propertyModalOpen} toggle={togglePropertyModal} action={handleAddProperty} title="Add New Property" button='Add' size='lg'>
                    <Form>
                        <Row className="mt-3">
                            <Col sm='6'><FormGroup><Label>Property Name</Label><Input onChange={(e) => setName(e.target.value)} /></FormGroup></Col>
                            <Col sm='6'><FormGroup><Label>Address</Label><Input type="textarea" rows={3} onChange={(e) => setAddress(e.target.value)} /></FormGroup></Col>
                            <Col sm='6'><FormGroup><Label>Contact Name</Label><Input onChange={(e) => setContactName(e.target.value)} /></FormGroup></Col>
                            <Col sm='6'><FormGroup><Label>Email</Label><Input type="email" onChange={(e) => setEmail(e.target.value)} /></FormGroup></Col>
                        </Row>
                    </Form>
                </Modal>

                <Modal isOpen={editModalOpen} toggle={toggleEditModal} action={handleEditProperty} title="Edit Property" button="Update" size="lg">
                    <Form>
                        <Row className="mt-3">
                            <Col sm='6'><FormGroup><Label>Property Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></FormGroup></Col>
                            <Col sm='6'><FormGroup><Label>Address</Label><Input type="textarea" rows={3} value={address} onChange={(e) => setAddress(e.target.value)} /></FormGroup></Col>
                            <Col sm='6'><FormGroup><Label>Contact Name</Label><Input value={contactName} onChange={(e) => setContactName(e.target.value)} /></FormGroup></Col>
                            <Col sm='6'><FormGroup><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></FormGroup></Col>
                        </Row>
                    </Form>
                </Modal>

                <Modal isOpen={appAccessModalOpen} toggle={toggleAppAccessModal} action={handleAppAccess} title="Grant App Access" button="Grant Access">
                    <Form>
                        <Row className="mt-3">
                            <Col sm='12'>
                                <FormGroup>
                                    <Label>Select App</Label>
                                    <Input type="select" onChange={(e) => setAppId(e.target.value)}>
                                        <option value="">Select an app</option>
                                        {apps.map((app: any) => (
                                            <option key={app.id} value={app.app_id}>{app.name}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>
                    </Form>
                </Modal>
            </Container>
        </>
    );
}
